import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Lib
import apiClient from "@/lib/apiClient";

// UI
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";

// Icons
import { Question, Exam } from "@/components/icons";

// Components
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";

// Hooks
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";

// Styles
import styles from "./ExamDifficultyReportPage.module.scss";

// ---------------------------------------------------------------------------
// API shape (local — backend hazır, route: /api/admin/reports/exam-difficulty)
// ---------------------------------------------------------------------------

type ExamDifficultyClassification = "easy" | "moderate" | "hard" | "very-hard";

interface ExamDifficultyDistractor {
  optionText: string;
  pickRatePercent: number;
  isCorrect: boolean;
}

interface ExamDifficultyItem {
  questionID: string;
  questionText: string;
  examID: string;
  examTitle: string;
  attempts: number;
  correctCount: number;
  correctRatePercent: number;
  avgTimeSeconds: number;
  classification: ExamDifficultyClassification;
  distractor: ExamDifficultyDistractor[];
}

interface ExamDifficultyResponse {
  summary: {
    totalQuestions: number;
    totalAttempts: number;
    avgCorrectRate: number;
    hardestRate: number;
    easiestRate: number;
    problematicCount: number;
  };
  byClassification: { label: ExamDifficultyClassification; count: number }[];
  difficultyHistogram: { label: string; count: number }[];
  hardestTop10: { label: string; value: number }[];
  easiestTop10: { label: string; value: number }[];
  items: ExamDifficultyItem[];
  count: number;
}

interface ExamDifficultyParams {
  page?: number;
  limit?: number;
  search?: string;
  examID?: string;
  classification?: ExamDifficultyClassification;
}

const PAGE_SIZE = 20;
const ENDPOINT = "/api/admin/reports/exam-difficulty";

/** TransformResponseInterceptor `{ data: <payload> }` döner — güvenli unwrap. */
const fetchExamDifficulty = async (
  params: ExamDifficultyParams,
  signal?: AbortSignal,
): Promise<ExamDifficultyResponse> => {
  const { data } = await apiClient.get(ENDPOINT, { params, signal });
  return (data?.data ?? data) as ExamDifficultyResponse;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const classificationStyle = (
  t: (k: string) => string,
): Record<
  ExamDifficultyClassification,
  { label: string; color: string; bg: string }
> => ({
  easy: {
    label: t("reports.examDifficulty.easy"),
    color: "#166534",
    bg: "#dcfce7",
  },
  moderate: {
    label: t("reports.examDifficulty.moderate"),
    color: "#1e40af",
    bg: "#dbeafe",
  },
  hard: {
    label: t("reports.examDifficulty.hard"),
    color: "#92400e",
    bg: "#fef3c7",
  },
  "very-hard": {
    label: t("reports.examDifficulty.veryHard"),
    color: "#b91c1c",
    bg: "#fee2e2",
  },
});

const CLASSIFICATION_COLORS: Record<ExamDifficultyClassification, string> = {
  easy: "#10b981",
  moderate: "#0ea5e9",
  hard: "#f59e0b",
  "very-hard": "#ef4444",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ExamDifficultyReportPage: React.FC = () => {
  const { t } = useTranslation();
  const styleMap = useMemo(() => classificationStyle(t), [t]);
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState<number>(1);

  const params: ExamDifficultyParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: filter.search?.trim() || undefined,
    }),
    [filter.search, page],
  );

  const { data, isLoading, error } = useReportData<
    ExamDifficultyResponse,
    ExamDifficultyParams
  >({
    fetcher: (p, signal) => fetchExamDifficulty(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  // ---------------- Charts: data prep ----------------
  const donutLabels = useMemo(
    () =>
      (data?.byClassification ?? []).map((b) => styleMap[b.label].label),
    [data?.byClassification, styleMap],
  );
  const donutSeries = useMemo(
    () => (data?.byClassification ?? []).map((b) => b.count),
    [data?.byClassification],
  );
  const donutColors = useMemo(
    () =>
      (data?.byClassification ?? []).map(
        (b) => CLASSIFICATION_COLORS[b.label],
      ),
    [data?.byClassification],
  );

  const histogramCategories = useMemo(
    () => (data?.difficultyHistogram ?? []).map((b) => b.label),
    [data?.difficultyHistogram],
  );
  const histogramData = useMemo(
    () => (data?.difficultyHistogram ?? []).map((b) => b.count),
    [data?.difficultyHistogram],
  );

  const hardestCategories = useMemo(
    () => (data?.hardestTop10 ?? []).map((p) => p.label),
    [data?.hardestTop10],
  );
  const hardestData = useMemo(
    () => (data?.hardestTop10 ?? []).map((p) => p.value),
    [data?.hardestTop10],
  );

  // ---------------- Table columns ----------------
  const columns: TableColumn<ExamDifficultyItem>[] = [
    {
      key: "questionText",
      label: t("reports.examDifficulty.colQuestion"),
      header: t("reports.examDifficulty.colQuestion"),
      accessor: (row) => (
        <span className={styles.questionCell} title={row.questionText}>
          {row.questionText}
        </span>
      ),
    },
    {
      key: "examTitle",
      label: t("reports.examDifficulty.colExam"),
      header: t("reports.examDifficulty.colExam"),
      accessor: (row) => <span className={styles.examCell}>{row.examTitle}</span>,
    },
    {
      key: "attempts",
      label: t("reports.examDifficulty.colAttempts"),
      header: t("reports.examDifficulty.colAttempts"),
      accessor: (row) => <span className={styles.numCell}>{row.attempts}</span>,
    },
    {
      key: "correctCount",
      label: t("reports.examDifficulty.colCorrect"),
      header: t("reports.examDifficulty.colCorrect"),
      accessor: (row) => <span className={styles.numCell}>{row.correctCount}</span>,
    },
    {
      key: "correctRatePercent",
      label: t("reports.examDifficulty.colCorrectPct"),
      header: t("reports.examDifficulty.colCorrectPct"),
      accessor: (row) => (
        <span className={styles.numCell}>{row.correctRatePercent.toFixed(1)}%</span>
      ),
    },
    {
      key: "avgTimeSeconds",
      label: t("reports.examDifficulty.colAvgTime"),
      header: t("reports.examDifficulty.colAvgTime"),
      accessor: (row) => <span className={styles.numCell}>{row.avgTimeSeconds}</span>,
    },
    {
      key: "classification",
      label: t("reports.examDifficulty.colClassification"),
      header: t("reports.examDifficulty.colClassification"),
      accessor: (row) => {
        const c = styleMap[row.classification];
        return (
          <span
            className={styles.badge}
            style={{ background: c.bg, color: c.color }}
          >
            {c.label}
          </span>
        );
      },
    },
  ];

  // ---------------- Render ----------------
  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<ExamDifficultyItem>({
    reportKey: "EXAM_DIFFICULTY",
    reportTitle: t("reports.examDifficulty.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.examDifficulty.title")}
      layoutKey={`report:EXAM_DIFFICULTY`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reports.examDifficulty.category")}
      description={t("reports.examDifficulty.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="EXAM_DIFFICULTY"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="sinav-zorluk"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <div className={styles.filters}>
          <ReportFilterBar
            value={filter}
            onChange={(v) => {
              setFilter(v);
              setPage(1);
            }}
            showDateRange={false}
            showDepartment={false}
          />
        </div>
      }
    >
      {/* KPIs */}
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.examDifficulty.kpiTotalQuestions")}
          value={summary?.totalQuestions ?? 0}
          icon={<Question width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.examDifficulty.kpiAvgCorrect")}
          value={
            summary ? `${summary.avgCorrectRate.toFixed(1)}%` : "—"
          }
          icon={<Exam width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.examDifficulty.kpiHardestRate")}
          value={summary ? `${summary.hardestRate.toFixed(1)}%` : "—"}
          icon={<Question width={18} height={18} />}
          accent="warning"
        />
        <ReportKPICard
          title={t("reports.examDifficulty.kpiProblematic")}
          value={summary?.problematicCount ?? 0}
          icon={<Question width={18} height={18} />}
          accent="danger"
        />
      </div>

      {/* Charts row */}
      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.examDifficulty.chartDistributionTitle")}
          subtitle={t("reports.examDifficulty.chartDistributionSubtitle")}
          type="donut"
          isLoading={isLoading}
          isEmpty={donutSeries.every((v) => v === 0)}
          series={donutSeries}
          options={{
            labels: donutLabels,
            colors: donutColors,
            legend: { position: "bottom" },
            dataLabels: { enabled: true },
          }}
        />
        <ReportChart
          title={t("reports.examDifficulty.chartHistogramTitle")}
          subtitle={t("reports.examDifficulty.chartHistogramSubtitle")}
          type="bar"
          isLoading={isLoading}
          isEmpty={histogramData.every((v) => v === 0)}
          series={[
            {
              name: t("reports.examDifficulty.chartHistogramSeries"),
              data: histogramData,
            },
          ]}
          options={{
            xaxis: { categories: histogramCategories, title: { text: "%" } },
            colors: ["#4f46e5"],
            plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
            dataLabels: { enabled: false },
          }}
        />
      </div>

      <div className={styles.chartGridFull}>
        <ReportChart
          title={t("reports.examDifficulty.chartHardestTitle")}
          subtitle={t("reports.examDifficulty.chartHardestSubtitle")}
          type="bar"
          isLoading={isLoading}
          isEmpty={hardestData.length === 0}
          series={[
            {
              name: t("reports.examDifficulty.chartHardestSeries"),
              data: hardestData,
            },
          ]}
          options={{
            xaxis: { categories: hardestCategories, max: 100 },
            colors: ["#ef4444"],
            plotOptions: {
              bar: { horizontal: true, barHeight: "65%", borderRadius: 4 },
            },
            dataLabels: {
              enabled: true,
              formatter: (val: number) => `${val.toFixed(1)}%`,
            },
          }}
          height={Math.max(280, 36 * hardestData.length + 80)}
        />
      </div>

      {/* Items table */}
      {cfg.columnSettings}
      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.questionID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.examDifficulty.noData")
            : t("reports.examDifficulty.noQuestion")
        }
      />
    </ReportDetailLayout>
  );
};

export default ExamDifficultyReportPage;
